import {
  Box,
  Button,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HeroBackground1 } from "../../../../asset/images/LandingPage";
import { useAuth } from "../../../../common/provider";
import PTTranslate from "../../../../common/components/ui/PTTranslate";

const HeroSection = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleInscriptionDialog } = useAuth();

  const onDownload = (fileName) => {
    let link = document.createElement("a");
    link.href = `/docs/${fileName}`;
    link.style = "visibility:hidden";
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Box
      sx={{
        minHeight: { xs: "70vh", sm: "80vh", md: "90vh", lg: "100vh" },
        width: "100%",
        background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${HeroBackground1})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: "100%", px: { xs: 2, sm: 3, md: 6, lg: 10 } }}
      >
        <Box
          sx={{
            color: "white",
            textAlign: "left",
            // Add top padding to prevent collision with navbar
            pt: { xs: 15, sm: 17, md: 19, lg: 19 },
            pb: { xs: 7, sm: 9, md: 11, lg: 13 },
            maxWidth: { xs: "100%", sm: "95%", md: "85%", lg: "80%", xl: "80%" },
            ml: 0,
            pr: { xs: 1, sm: 2, md: 3, lg: 4 },
          }}
        >
          {/* Title that adapts naturally to available space */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: "#fff",
              mb: { xs: 0.5, sm: 1 },
              fontSize: { 
                xs: "1.5rem",
                sm: "2.5rem", 
                md: "4rem", 
                lg: "5rem", 
                xl: "5.5rem" 
              },
              fontWeight: 600,
              lineHeight: 1.1,
              width: "100%",
              // Allow text to flow naturally but with max-width constraints
              maxWidth: { 
                xs: "100%", 
                sm: "100%", 
                md: "100%", 
                lg: "100%"
              },
              // Don't enforce line breaks, let text flow naturally
              whiteSpace: "normal",
              overflowWrap: "break-word",
              letterSpacing: "1px"
            }}
          >
            <PTTranslate tk="hero.title" /> <PTTranslate tk="hero.titleSecondLine" />
          </Typography>

          {/* Subtitle that also adapts naturally */}
          <Typography
            variant="body1"
            sx={{
              mb: { xs: 2, sm: 2.5, md: 3 },
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: { 
                xs: "0.85rem",
                sm: "1.1rem", 
                md: "1.5rem", 
                lg: "1.8rem", 
                xl: "2rem" 
              },
              fontWeight: 400,
              lineHeight: 1.3,
              // Allow text to flow naturally
              whiteSpace: "normal",
              "& .highlight": {
                fontWeight: 600,
                color: "#fff",
                ml: 1
              }
            }}
          >
            <PTTranslate tk="hero.subtitle" /> <PTTranslate tk="hero.subtitleSecondLine" />
            <Box component="span" className="highlight">
              <PTTranslate tk="hero.subtitleHighlight" />
            </Box>
          </Typography>

          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 2, lg: 3 },
            mt: { xs: 1.5, sm: 2, md: 3, lg: 4 }
          }}>
            <Button
              variant="contained"
              onClick={toggleInscriptionDialog}
              sx={{
                backgroundColor: "#fff",
                color: "#333",
                px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
                py: { xs: 1, sm: 1.2, md: 1.5, lg: 1.8 },
                borderRadius: "30px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                },
                textTransform: "none",
                fontSize: { 
                  xs: "0.8rem", 
                  sm: "0.9rem", 
                  md: "1.1rem", 
                  lg: "1.3rem", 
                  xl: "1.4rem" 
                },
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                width: { xs: "fit-content", sm: "auto" }
              }}
            >
              <PTTranslate tk="hero.getStartedButton" />
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => {
                onDownload("Manufacturer Portal - User Manual.pdf");
              }}
              sx={{
                borderColor: "#fff",
                color: "#fff",
                px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
                py: { xs: 1, sm: 1.2, md: 1.5, lg: 1.8 },
                borderRadius: "30px",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                },
                textTransform: "none",
                fontSize: { 
                  xs: "0.8rem", 
                  sm: "0.9rem", 
                  md: "1.1rem", 
                  lg: "1.3rem", 
                  xl: "1.4rem" 
                },
                fontWeight: 500,
                transition: "all 0.3s ease",
                width: { xs: "fit-content", sm: "auto" }
              }}
            >
              <PTTranslate tk="hero.userGuideButton" />
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;